/**
 * Messages entre onglets de l'application : ouverture et fermeture de session,
 * file modifiée, envoi terminé. Ils ne portent jamais ni jeton ni contenu de
 * saisie — des identifiants tout au plus. `BroadcastChannel` n'est créé qu'au
 * premier usage et la diffusion reste inerte là où il manque.
 */

export type MessageOnglets =
  | { type: 'file-modifiee' }
  | { type: 'envoi-termine'; utilisateurId: string; contactId: string; issue: 'envoye' | 'refuse' }
  | { type: 'brouillon-ecrit'; utilisateurId: string; contactId: string; onglet: string }
  | { type: 'session-ouverte'; utilisateurId: string }
  /** `code` : refus de l'API qui a fermé la session (COMPTE_INACTIF : plus aucun brouillon). */
  | { type: 'session-fermee'; motif: 'deconnexion' | 'perdue'; code?: string }
  | { type: 'mise-a-jour-requise' };

export interface Diffusion {
  publier(message: MessageOnglets): void;
  ecouter(ecouteur: (message: MessageOnglets) => void): () => void;
  fermer(): void;
}

const NOM_CANAL = 'rh-app';

const texte = (valeur: unknown): valeur is string => typeof valeur === 'string' && valeur !== '';

/**
 * Lecture défensive : un onglet resté sur une autre version de l'application
 * peut publier des messages que celle-ci ne connaît pas. Ils sont ignorés.
 */
export function lireMessage(brut: unknown): MessageOnglets | null {
  if (typeof brut !== 'object' || brut === null) return null;
  const m = brut as Record<string, unknown>;
  switch (m.type) {
    case 'file-modifiee':
    case 'mise-a-jour-requise':
      return { type: m.type };
    case 'envoi-termine':
      return texte(m.utilisateurId) && texte(m.contactId) && (m.issue === 'envoye' || m.issue === 'refuse')
        ? { type: m.type, utilisateurId: m.utilisateurId, contactId: m.contactId, issue: m.issue }
        : null;
    case 'brouillon-ecrit':
      return texte(m.utilisateurId) && texte(m.contactId) && texte(m.onglet)
        ? { type: m.type, utilisateurId: m.utilisateurId, contactId: m.contactId, onglet: m.onglet }
        : null;
    case 'session-ouverte':
      return texte(m.utilisateurId) ? { type: m.type, utilisateurId: m.utilisateurId } : null;
    case 'session-fermee':
      return m.motif === 'deconnexion' || m.motif === 'perdue'
        ? { type: m.type, motif: m.motif, ...(texte(m.code) ? { code: m.code } : {}) }
        : null;
    default:
      return null;
  }
}

/** Distribue sans qu'un écouteur en faute prive les suivants du message. */
function distribuer(ecouteurs: ReadonlySet<(message: MessageOnglets) => void>, message: MessageOnglets): void {
  for (const ecouteur of [...ecouteurs]) {
    try {
      ecouteur(message);
    } catch (erreur) {
      console.error(erreur);
    }
  }
}

function diffusionCanal(): Diffusion {
  let canal: BroadcastChannel | null = null;
  const ecouteurs = new Set<(message: MessageOnglets) => void>();

  function ouvrir(): BroadcastChannel | null {
    if (canal) return canal;
    if (typeof BroadcastChannel === 'undefined') return null;
    try {
      canal = new BroadcastChannel(NOM_CANAL);
    } catch {
      return null;
    }
    canal.onmessage = (evenement: MessageEvent) => {
      const message = lireMessage(evenement.data);
      if (message) distribuer(ecouteurs, message);
    };
    return canal;
  }

  return {
    publier(message) {
      try {
        ouvrir()?.postMessage(message);
      } catch {
        /* canal fermé par le navigateur (page en cache) : message perdu, sans gravité */
      }
    },
    ecouter(ecouteur) {
      ecouteurs.add(ecouteur);
      ouvrir();
      return () => {
        ecouteurs.delete(ecouteur);
      };
    },
    fermer() {
      canal?.close();
      canal = null;
      ecouteurs.clear();
    },
  };
}

/** Réseau de diffusions en mémoire, isolé des autres. */
export interface BusMemoire {
  readonly membres: Set<Set<(message: MessageOnglets) => void>>;
}

export function busMemoire(): BusMemoire {
  return { membres: new Set() };
}

const busParDefaut = busMemoire();

/**
 * Tests : les autres instances créées sur le même bus reçoivent, jamais
 * l'émettrice (comme `BroadcastChannel`), après une copie et une microtâche
 * (comme un vrai passage entre onglets, et insensible aux faux minuteurs).
 */
export function diffusionMemoire(bus: BusMemoire = busParDefaut): Diffusion {
  const ecouteurs = new Set<(message: MessageOnglets) => void>();
  bus.membres.add(ecouteurs);
  return {
    publier(message) {
      const copie = JSON.stringify(message);
      for (const membre of bus.membres) {
        if (membre === ecouteurs) continue;
        queueMicrotask(() => {
          const recu = lireMessage(JSON.parse(copie));
          if (recu && bus.membres.has(membre)) distribuer(membre, recu);
        });
      }
    },
    ecouter(ecouteur) {
      ecouteurs.add(ecouteur);
      return () => {
        ecouteurs.delete(ecouteur);
      };
    },
    fermer() {
      ecouteurs.clear();
      bus.membres.delete(ecouteurs);
    },
  };
}

let courante: Diffusion | null = null;

/** Diffusion de l'onglet (une seule instance, donc jamais d'écho à soi-même). */
export function obtenirDiffusion(): Diffusion {
  courante ??= diffusionCanal();
  return courante;
}

/** Tests : remplace la diffusion de l'onglet (`null` revient au canal réel, créé à la demande). */
export function remplacerDiffusion(diffusion: Diffusion | null): void {
  courante = diffusion;
}
